/* eslint-disable react/prop-types */
import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import { useHistory } from 'react-router';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { flowRight } from 'lodash';
import FocusTrap from 'focus-trap-react';
import {
  RecommendationAttemptStatusDto,
  RecommendationContentTypeDto,
  RemediationActivityTypeDto,
  StatusWithDueDate
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  ELSButton,
  ELSDataTable
} from '../../components/els.components';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { RoutePath } from '../../components/app/app.constants';
import { AppContext } from '../../components/app/AppProvider.component';
import { ContentTypeDisplay } from '../../components/content-type-display/ContentTypeDisplay.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import ActivityLogFilterModal, { ActivityLogFilterModalId } from './ActivityLogFilterModal.component';
import {
  EmptyValueDisplay,
  NotApplicableDisplay
} from './remediation-student-activity.constants';
import {
  ActivityLogTableData,
  ActivityProgressApplyScoreFilter
} from './remediation-student-activity.models';
import { StatusPill } from '../../components/status-pill/StatusPill.component';
import {
  getAssessmentGoals,
  getAssessmentSubmissionByAttempt,
  getAttemptStatus,
  getGoalFromRecommendation,
  getRecommendationFromAttempt,
  handleNavigateToContentPerformance,
} from '../../utilities/remediation.utilities';
import IsRender from '../../components/is-render/IsRender.component';
import { AssessmentSubmissionDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { RemediationContentTypeConfigMap } from '../../models/remediation.models';
import {
  filterActivityByActivityName,
  filterActivityBySelectedActivityType,
  filterActivityBySelectedContentType,
  filterActivityBySelectedStatus,
  filterByDateRange,
  getActivityLogTimeSpentDisplay,
  getCorrectQuestionCount,
  getLastDateFromQuestionSubmissionDetail,
  getQuestionSubmissionDetailsForAttempt,
  getRecActivityType,
  getSubmittedDate,
  isRecommendationInProgress,
  showAttemptViewButton
} from './remediation-student-activity.utilities';
import { StatusDisplayMap } from '../../constants/remediation.constants';
import { ActivityLogFilterPills } from './ActivityLogFilterPills.component';
import {
  trackAdobeAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';

export type ActivityLogPropsOnly = {
  selectedGoalId: string;
  handleResetSelectedGoal: () => void;
}

export type ActivityLogProps = ActivityLogPropsOnly & ELSPropsFromModalService;

export type ActivityLogState = {
  selectedActivityTypes: RemediationActivityTypeDto[];
  selectedContentTypes: RecommendationContentTypeDto[];
  selectedStatuses: StatusWithDueDate[];
  selectedGoalId: string;
  startDate: Date;
  endDate: Date;
}

export const defaultActivityLogState: ActivityLogState = {
  selectedActivityTypes: [],
  selectedContentTypes: [],
  selectedStatuses: [],
  selectedGoalId: null,
  startDate: null,
  endDate: null,
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const ActivityLogComponent = (props: ActivityLogProps) => {
  const [state, setState] = useState<ActivityLogState>(defaultActivityLogState);

  useEffect(() => {
    setState({
      ...defaultActivityLogState,
      selectedGoalId: props.selectedGoalId
    });
  }, [props.selectedGoalId]);

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;
  const history = useHistory();

  if (!baseState || !baseState.eolsAssessment) {
    return null;
  }

  const {
    recommendationAttempts,
    recommendations,
  } = baseState;

  const handleApplyFilters = (_props: {
    selectedActivityTypes: RemediationActivityTypeDto[];
    selectedContentTypes: RecommendationContentTypeDto[];
    selectedGoalId: string;
    selectedStatuses: StatusWithDueDate[];
    startDate: Date;
    endDate: Date;
  }) => {
    if (!_props.selectedGoalId) {
      props.handleResetSelectedGoal();
    }
    setState(_props);
  };

  const handleFilterModalClick = () => {
    const {
      modalService,
    } = props;

    if (!recommendations || !recommendationAttempts) {
      return;
    }

    modalService.openCustomModal({
      modalId: ActivityLogFilterModalId,
      noFocusTrap: true,
      modal: (
        <FocusTrap>
          <ActivityLogFilterModal
            modalService={modalService}
            selectedActivityTypes={state.selectedActivityTypes}
            selectedContentTypes={state.selectedContentTypes}
            selectedGoalId={state.selectedGoalId}
            selectedStatuses={state.selectedStatuses}
            startDate={state.startDate}
            endDate={state.endDate}
            handleApplyFilters={({
              selectedActivityTypes,
              selectedContentTypes,
              selectedGoalId,
              selectedStatuses,
              startDate,
              endDate
            }) => handleApplyFilters({
              selectedActivityTypes,
              selectedContentTypes,
              selectedGoalId,
              selectedStatuses,
              startDate,
              endDate
            })}
          />
        </FocusTrap>
      )
    });
  };

  const areFiltersApplied = (): boolean => {
    return !!(state.selectedGoalId
      || state.startDate
      || state.endDate
      || state.selectedStatuses.length > 0
      || state.selectedContentTypes.length > 0
      || state.selectedActivityTypes.length > 0);
  };

  const getActivitiesDisplayLanguage = (filteredActivityLogRows: ActivityLogTableData[], allActivityLogRows: ActivityLogTableData[]): string => {
    if (areFiltersApplied()) {
      return `Displaying ${filteredActivityLogRows.length} of ${allActivityLogRows.length} activities`;
    }

    return 'Displaying all activities';
  };

  const getActivityStatusDisplay = (activity: ActivityLogTableData) => {
    if (!activity || !activity.recommendationAttempt) {
      return null;
    }

    if (activity.recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
      return NotApplicableDisplay;
    }

    const status = getAttemptStatus(activity.recommendationAttempt, baseState);

    return (
      <StatusPill status={status}>
        {StatusDisplayMap[status]}
      </StatusPill>
    );
  };

  const getActivityScoreDisplay = (activity: ActivityLogTableData, attemptAssessmentSubmissions: AssessmentSubmissionDto[]): string => {

    if (!activity) {
      return null;
    }

    const {
      recommendationAttempt,
      questionSubmissionDetail
    } = activity;

    const {
      assessmentGoalType
    } = activity.recommendation;

    if (assessmentGoalType === RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE) {

      if (
        !questionSubmissionDetail
        || !questionSubmissionDetail.questions
        || questionSubmissionDetail.questions.length === 0
      ) {
        return EmptyValueDisplay;
      }

      return `${getCorrectQuestionCount(questionSubmissionDetail)}/${questionSubmissionDetail.questions.length} correct`;
    }

    if (!RemediationContentTypeConfigMap[activity.recommendation.contentType].isScored) {
      return NotApplicableDisplay;
    }

    if (
      [
        RecommendationAttemptStatusDto.IN_PROGRESS,
        RecommendationAttemptStatusDto.NOT_STARTED
      ].includes(recommendationAttempt.status)
    ) {
      return EmptyValueDisplay;
    }

    const submission = getAssessmentSubmissionByAttempt(recommendationAttempt, attemptAssessmentSubmissions);
    if (!submission) {
      return null;
    }
    const score = Math.round((submission.totalCorrect / submission.totalAnswered) * 100);
    return `${score}%`;

  };

  const getActivitySubmittedDateDisplay = (activity: ActivityLogTableData): string => {
    if (!activity) {
      return null;
    }

    if (isRecommendationInProgress(activity.recommendation, baseState) && activity.recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
      return EmptyValueDisplay;
    }

    return getSubmittedDate(activity);
  };

  const handleViewActivityClick = (activity: ActivityLogTableData) => {
    const isRecScored = RemediationContentTypeConfigMap[activity.recommendation.contentType].isScored;

    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:viewActivity:${activity.recommendation.assessmentGoalType}`,
        }
      }
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.CONTENT_VIEW_CLICKED,
        props: {
          recommendationId: activity.recommendation.id,
          recommendationAttemptId: isRecScored ? activity.recommendationAttempt?.id : undefined,
          contentType: activity.recommendation.contentType,
          viewContext: 'activityLog',
          studentUserId: baseState.raasAssessment?.userId,
          remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
          assessmentId: baseState.eolsAssessment?.id
        }
      }
    });

    handleNavigateToContentPerformance({
      appState,
      appStateRef,
      setAppState,
      recommendation: activity.recommendation,
      recommendationAttempt: isRecScored ? activity.recommendationAttempt : null,
      quizSession: null,
      history,
      ref: RoutePath.REMEDIATION_STUDENT_DETAIL,
      assessment: baseState.eolsAssessment,
    });
  };

  const getStudentActivityViewBtn = (activity: ActivityLogTableData) => {
    const isShowAttemptViewButton = showAttemptViewButton({
      recommendation: activity.recommendation,
      recommendationAttempts: baseState.recommendationAttempts,
      attemptAssessmentSubmissions: baseState.attemptAssessmentSubmissions,
      attemptFilter: ActivityProgressApplyScoreFilter.BEST_ATTEMPT,
    });

    if (activity.recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ || !isShowAttemptViewButton) {
      return null;
    }

    return (
      <ELSButton
        type={ELSButtonType.SECONDARY}
        size={ELSButtonSize.SMALL}
        onClick={() => handleViewActivityClick(activity)}
      >
        View
      </ELSButton>
    );
  };
  // Need to create specific object to enable showing each quiz submission
  const getActivityLogRows = (): ActivityLogTableData[] => {
    if (!recommendationAttempts || !recommendationAttempts.length) {
      return null;
    }

    const { quizAttemptDetails } = baseState;

    return recommendationAttempts.reduce<ActivityLogTableData[]>((acc, attempt) => {
      const recommendation = getRecommendationFromAttempt(recommendations, attempt);
      const goal = getGoalFromRecommendation(getAssessmentGoals(baseState.eolsAssessment), recommendation);

      if (recommendation) {
        if (recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
          acc.push({
            recommendation,
            goal,
            recommendationAttempt: attempt,
            questionSubmissionDetail: null
          });
        } else {
          const questionSubmissionDetails = getQuestionSubmissionDetailsForAttempt(attempt, quizAttemptDetails);
          if (!questionSubmissionDetails || questionSubmissionDetails.length === 0) {
            return acc;
          }

          acc.push(...questionSubmissionDetails.map((questionSubmissionDetail) => ({
            recommendation,
            goal,
            recommendationAttempt: attempt,
            questionSubmissionDetail
          })));
        }
      }

      return acc;
    }, []);
  };

  const getFilteredActivityLogRows = (activityLogRows: ActivityLogTableData[]): ActivityLogTableData[] => {
    if (!activityLogRows || !activityLogRows.length) {
      return null;
    }

    const filteredByActivityType = filterActivityBySelectedActivityType(activityLogRows, state.selectedActivityTypes);
    const filteredByContentType = filterActivityBySelectedContentType(filteredByActivityType, state.selectedContentTypes);
    const filteredByStatus = filterActivityBySelectedStatus(filteredByContentType, baseState, state.selectedStatuses);
    const filteredByActivityName = filterActivityByActivityName(filteredByStatus, state.selectedGoalId);
    const filteredByDate = filterByDateRange(filteredByActivityName, state.startDate, state.endDate, baseState);

    return filteredByDate.sort((a, b) => {
      const dateA = a.recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ
        ? new Date(a.recommendationAttempt.updatedAt)
        : new Date(getLastDateFromQuestionSubmissionDetail(a.questionSubmissionDetail));

      const dateB = b.recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ
        ? new Date(b.recommendationAttempt.updatedAt)
        : new Date(getLastDateFromQuestionSubmissionDetail(b.questionSubmissionDetail));

      return dateB.getTime() - dateA.getTime();
    });
  };

  const getRecommendationActivityName = (activity: ActivityLogTableData) => {
    const recommendationTitle = activity.recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ
      ? 'Quiz Questions'
      : activity.recommendation.title;
    return (
      <div>
        {recommendationTitle}
        <span className="u-els-color-n7">
          <ContentTypeDisplay contentType={activity.recommendation.contentType}/>
        </span>
      </div>
    );
  };

  const allActivityLogRows = getActivityLogRows();
  const filteredActivityLogRows = getFilteredActivityLogRows(allActivityLogRows);

  const doStartedAttemptsExist = allActivityLogRows && allActivityLogRows.length > 0;
  const doFilteredAttemptsExist = filteredActivityLogRows && filteredActivityLogRows.length > 0;

  return (
    <>
      <div className="u-els-margin-top-2x u-els-margin-bottom-1x1o2">
        <FlexLayout
          isRender={doStartedAttemptsExist}
          modifiers={[FlexLayoutModifier.SPACE_BETWEEN]}
        >
          <FlexItem>
            <span className="u-els-font-size-body-large u-els-display-block u-els-margin-bottom-1o2">
              {getActivitiesDisplayLanguage(filteredActivityLogRows, allActivityLogRows)}
            </span>
            <div>
              <ActivityLogFilterPills
                selectedActivityTypes={state.selectedActivityTypes}
                selectedContentTypes={state.selectedContentTypes}
                selectedStatuses={state.selectedStatuses}
                selectedGoalId={state.selectedGoalId}
                startDate={state.startDate}
                endDate={state.endDate}
                assessmentGoals={getAssessmentGoals(baseState.eolsAssessment)}
                handleApplyFilters={handleApplyFilters}
              />
            </div>
          </FlexItem>
          <FlexItem>
            <ELSButton
              type={ELSButtonType.SECONDARY}
              size={ELSButtonSize.SMALL}
              className="c-nra-activity-log__secondary-btn-text"
              onClick={() => {
                handleFilterModalClick();
              }}>
              <span className="u-els-bold">
                Filter
              </span>
            </ELSButton>
          </FlexItem>
        </FlexLayout>
      </div>

      <IsRender isRender={!doFilteredAttemptsExist}>
        <div className="u-els-font-size-body-large u-els-text-center u-els-margin-top-2x">
          No activities found
        </div>
      </IsRender>
      <IsRender isRender={doFilteredAttemptsExist}>
        <ELSDataTable
          data={filteredActivityLogRows}
          noWrap
          columns={[
            {
              header: 'Activity Name',
              customRender: (activity: ActivityLogTableData) => getRecommendationActivityName(activity),
            },
            {
              header: 'Activity Type',
              customRender: (activity: ActivityLogTableData) => getRecActivityType(activity.recommendationAttempt, baseState.recommendations)
            }, {
              header: 'Score',
              customRender: (activity: ActivityLogTableData) => getActivityScoreDisplay(activity, baseState.attemptAssessmentSubmissions)
            }, {
              header: 'Time Spent',
              customRender: (activity: ActivityLogTableData) => getActivityLogTimeSpentDisplay({
                activityLogRow: activity,
                baseState
              })
            }, {
              header: 'Status',
              customRender: (activity: ActivityLogTableData) => getActivityStatusDisplay(activity)
            }, {
              header: 'Submitted',
              field: 'updatedAt',
              customRender: (activity: ActivityLogTableData) => getActivitySubmittedDateDisplay(activity)
            }, {
              header: ' ',
              customRender: (activity: ActivityLogTableData) => getStudentActivityViewBtn(activity)
            }]
          }
        >

        </ELSDataTable>
      </IsRender>
    </>
  );
};

const enhance = flowRight(ELSWithModalService);

const ActivityLog:React.FC<ActivityLogPropsOnly> = enhance(ActivityLogComponent);

export default ActivityLog;
